import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';

import { IconSelectorInput } from '@@common';

import { ParameterMetadata, ParameterType, PrimitiveType } from '../../../../api/api';
import { ExpressionInput } from '../../../FlowDesigner/Components/Expressions/ExpressionInput';
import { VariableScope } from '../../../FlowDesigner/Models/VariableScopes/VariableScope';
import { DataDisplayPropertyModel, PropertyDisplayFormat } from '../../Models/DataDisplayPropertyModel';
import { SortableCollapsibleItem } from './SortableCollapsibleItem';
import { SortableItemListDialog } from './SortableItemListDialog';

const FORMAT_OPTIONS: { value: PropertyDisplayFormat; label: string }[] = [
	{ value: 'Date', label: 'Date' },
	{ value: 'DateTime', label: 'Date & Time' },
	{ value: 'Currency', label: 'Currency' },
	{ value: 'Percent', label: 'Percent' },
	{ value: 'Number', label: 'Number' },
	{ value: 'Link', label: 'Link' },
	{ value: 'PhoneLink', label: 'Phone Link' },
	{ value: 'EmailLink', label: 'Email Link' },
];

const REQUIRED_PARAMETER_METADATA: ParameterMetadata = {
	name: 'Required',
	displayName: 'Required',
	order: 0,
	type: ParameterType.Expression,
	categories: [],
	isCollection: false,
	expectedType: { type: PrimitiveType.Boolean },
};

// ---- Property row content ----

export const DATA_DISPLAY_PROPERTY_COMPONENT_TYPE = 'DataDisplayProperty';
export const DATA_DISPLAY_PROPERTIES_PATH_SEGMENT = 'properties';

type PropertyEditorProps = {
	property: DataDisplayPropertyModel;
	onChange: (updated: DataDisplayPropertyModel) => void;
	variableScope?: VariableScope;
};

export function DataDisplayPropertyEditor({ property, onChange, variableScope }: PropertyEditorProps) {
	return (
		<>
			<Stack direction="row" spacing={2}>
				<TextField
					label="Label"
					value={property.Label}
					onChange={(e) => onChange({ ...property, Label: e.target.value })}
					size="small"
					required
					sx={{ flex: 1 }}
				/>
				<TextField
					label="Field"
					value={property.Field ?? ''}
					onChange={(e) => onChange({ ...property, Field: e.target.value || undefined })}
					size="small"
					sx={{ flex: 1 }}
				/>
			</Stack>

			<Stack direction="row" spacing={2}>
				<FormControl size="small" sx={{ flex: 1 }}>
					<InputLabel>Format</InputLabel>
					<Select
						label="Format"
						value={property.Format ?? ''}
						onChange={(e) => onChange({ ...property, Format: (e.target.value as PropertyDisplayFormat) || undefined })}
					>
						<MenuItem value="">
							<em>None</em>
						</MenuItem>
						{FORMAT_OPTIONS.map((opt) => (
							<MenuItem key={opt.value} value={opt.value}>
								{opt.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<TextField
					label="Value Template"
					value={property.ValueTemplate ?? ''}
					onChange={(e) => onChange({ ...property, ValueTemplate: e.target.value || undefined })}
					size="small"
					sx={{ flex: 1 }}
				/>
			</Stack>

			<Stack direction="row" spacing={2}>
				<TextField
					label="Color"
					value={property.Color ?? ''}
					onChange={(e) => onChange({ ...property, Color: e.target.value || undefined })}
					size="small"
					sx={{ flex: 1 }}
				/>
				<TextField
					label="Flex"
					type="number"
					value={property.Flex ?? ''}
					onChange={(e) => onChange({ ...property, Flex: e.target.value ? Number(e.target.value) : undefined })}
					size="small"
					sx={{ flex: 1 }}
				/>
				<TextField
					label="Size"
					type="number"
					value={property.Layout?.Size ?? ''}
					onChange={(e) => onChange({ ...property, Layout: { ...property.Layout, Size: e.target.value ? Number(e.target.value) : undefined } })}
					size="small"
					sx={{ flex: 1 }}
				/>
			</Stack>

			<IconSelectorInput label="Icon" value={property.IconName} onValueChange={(iconName) => onChange({ ...property, IconName: iconName })} />

			<ExpressionInput
				expression={property.Required}
				onExpressionChange={(expression) => onChange({ ...property, Required: expression })}
				parameterMetadata={REQUIRED_PARAMETER_METADATA}
				variableScope={variableScope}
			/>

			<FormControlLabel
				control={
					<Checkbox
						checked={property.IsReadOnly ?? false}
						size="small"
						onChange={(e) => onChange({ ...property, IsReadOnly: e.target.checked || undefined })}
					/>
				}
				label="Read Only"
			/>
		</>
	);
}

// ---- Dialog ----

type DataDisplayPropertiesInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	properties: DataDisplayPropertyModel[] | undefined;
	onPropertiesChange: (properties: DataDisplayPropertyModel[]) => void;
	variableScope?: VariableScope;
};

export function DataDisplayPropertiesInputDialog({ open, setOpen, properties, onPropertiesChange, variableScope }: DataDisplayPropertiesInputDialogProps) {
	return (
		<SortableItemListDialog
			open={open}
			setOpen={setOpen}
			title="Configure Properties"
			items={properties}
			onItemsChange={onPropertiesChange}
			createItem={() => ({ Label: '' }) as DataDisplayPropertyModel}
			addLabel="Add Property"
		>
			{(property, index, total, onChange, onMoveUp, onMoveDown, onDelete) => (
				<SortableCollapsibleItem
					key={index}
					title={property.Label || `Property ${index + 1}`}
					subtitle={property.Field}
					index={index}
					total={total}
					onMoveUp={onMoveUp}
					onMoveDown={onMoveDown}
					onDelete={onDelete}
				>
					<DataDisplayPropertyEditor property={property} onChange={onChange} variableScope={variableScope} />
				</SortableCollapsibleItem>
			)}
		</SortableItemListDialog>
	);
}
